/**
 * noteUiKit.jsx — Muhasebe FAZ 4.8, senet sekmelerinin ortak sunum parçaları.
 *
 * 5 iç sekme (Parçala/Birleştir · Ekle · İptal · Avukat İşlemleri · Düzenleme)
 * aynı form/uyarı/seçim desenini paylaşıyor; kopyalamak yerine burada tek
 * kaynakta toplandı. FAZ 3'te kurulan görsel dil (2px kenarlık, rounded-xl,
 * `border-b-4` katı gölge, `var(--border-light)` / `var(--brand-primary)`
 * token'ları — bkz. CollectionForm.jsx) AYNEN sürdürülüyor, yeni renk/ölçü
 * icat edilmiyor (design_guidelines.json).
 *
 * Erişilebilirlik (WCAG 2.1 AA):
 *   - `InlineAlert` variant'a göre `role="alert"` (danger/warning) ya da
 *     `role="status"` + `aria-live="polite"` (info/success) taşır.
 *   - `NotePicker` tekil seçimde native `<select>`, çoklu seçimde
 *     `role="group"` + gerçek `<input type="checkbox">` kullanır (klavye ile
 *     gezilebilir; özel div-checkbox taklidi YOK).
 *   - Zorunlu alanlar `aria-required`, hatalı alanlar `aria-invalid` +
 *     `aria-describedby` ile hata metnine bağlanır.
 */
import { AlertTriangle, Info, CheckCircle2, Loader2 } from 'lucide-react';
import { fmtMoneyAccounting } from '../../../utils/money';
import { getApiErrorCode, getApiErrorMessage } from '../../../utils/apiError';
import { promissoryStatusBadgeProps } from './columnDefs';
import { isStuckNote, splitLabel, normalizeDueDate } from './promissoryMath';

/**
 * Backend `error_code` -> yerel `accounting.errors.*` çevirisi; anahtar yoksa
 * backend'in kendi `message_tr`/`message_en`'i kullanılır (CollectionForm.jsx
 * ile AYNI desen — yeni bir hata sözlüğü kurulmuyor).
 *
 * `senet_taksit_kapatilamadi` ÖZEL bir haldir: işlem yarıda kalmış, senet
 * `splitting`/`merging` bırakılmıştır. Çağıran bunu ayrıca "muhasebe
 * mutabakatı gerekli" uyarısı ile göstermeli (bkz. isReconciliationError).
 *
 * ── BİLİNMEYEN KOD FALLBACK'İ ────────────────────────────────────────────────
 * Sözlükte olmayan bir `error_code` için `t(key)` ANAHTARIN KENDİSİNİ döner
 * (LanguageContext: `translations[lang]?.[key] || translations['en']?.[key] ||
 * key`); bu değer ekrana basılırsa kullanıcı "accounting.errors.foo_bar" gibi
 * ham bir metin görür. Bu yüzden anahtar çözülmediyse ASLA `t`'nin dönüşü
 * kullanılmaz. Sırayla:
 *   1. sözlükte karşılığı varsa yerelleştirilmiş metin,
 *   2. yoksa backend'in `message_tr`/`message_en` alanı,
 *   3. ikisi de yoksa (kod var ama mesaj yok) anlaşılır jenerik metin +
 *      destek için kodun kendisi — axios'un "Request failed with status code
 *      409" cümlesi kullanıcıya gösterilmez.
 */
export function noteErrorMessage(err, lang, t) {
  const code = getApiErrorCode(err);
  if (code) {
    const key = `accounting.errors.${code}`;
    const localized = t(key);
    if (localized !== key) return localized;
  }
  const detail = err?.response?.data?.detail;
  const serverMessage =
    detail && typeof detail === 'object' && !Array.isArray(detail)
      ? detail[`message_${lang}`] || detail.message_en || detail.message_tr
      : null;
  if (code && !serverMessage) {
    const fallbackKey = 'accounting.errors.unknown_with_code';
    const fallback = t(fallbackKey);
    if (fallback !== fallbackKey) return fallback.replace('{code}', code);
  }
  return getApiErrorMessage(err, lang);
}

export const RECONCILIATION_ERROR_CODE = 'senet_taksit_kapatilamadi';

/** İşlem yarıda kaldı mı (senet çapa duruma düştü mü)? */
export function isReconciliationError(err) {
  return getApiErrorCode(err) === RECONCILIATION_ERROR_CODE;
}

export const INPUT_CLASS =
  'w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-[var(--bg-app)] text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none disabled:opacity-50';

const ALERT_VARIANTS = {
  danger: { box: 'border-rose-300 bg-rose-50 text-rose-800', icon: AlertTriangle, role: 'alert' },
  warning: { box: 'border-amber-300 bg-amber-50 text-amber-800', icon: AlertTriangle, role: 'alert' },
  info: { box: 'border-sky-300 bg-sky-50 text-sky-800', icon: Info, role: 'status' },
  success: { box: 'border-emerald-300 bg-emerald-50 text-emerald-800', icon: CheckCircle2, role: 'status' },
};

/** Kutulu uyarı/bilgi bloğu. `title` kalın, `children` açıklama. */
export function InlineAlert({ variant = 'info', title, children, testId }) {
  const cfg = ALERT_VARIANTS[variant] || ALERT_VARIANTS.info;
  const Icon = cfg.icon;
  return (
    <div
      className={`flex items-start gap-2.5 rounded-xl border-2 p-3 ${cfg.box}`}
      role={cfg.role}
      aria-live={cfg.role === 'status' ? 'polite' : undefined}
      data-testid={testId}
    >
      <Icon size={18} className="flex-shrink-0 mt-0.5" aria-hidden />
      <div className="text-xs leading-relaxed">
        {title && <p className="font-bold mb-0.5">{title}</p>}
        {children}
      </div>
    </div>
  );
}

const BUTTON_VARIANTS = {
  primary:
    'border-[var(--brand-primary)] bg-[var(--brand-primary)] text-white hover:brightness-95 border-b-4',
  danger: 'border-rose-500 bg-rose-500 text-white hover:bg-rose-600 border-b-4 border-b-rose-700',
  success: 'border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600 border-b-4 border-b-emerald-700',
  neutral: 'border-[var(--border-light)] bg-white text-[var(--text-main)] hover:bg-gray-50',
};

/**
 * Katı gölgeli aksiyon butonu (FAZ 3 deseni).
 *
 * `...rest` (ör. `aria-label`) doğrudan `<button>`'a geçirilir — Muhasebe
 * FAZ 6 "Belgeler" turunda satır-içi Fatura PDF butonunun satır bazlı
 * `aria-label` alması gerektiği için eklendi (bkz. columnDefs.js
 * InvoicePdfButton). Mevcut çağıranlar (5 sekme) etkilenmez.
 */
export function ActionButton({
  variant = 'primary',
  type = 'button',
  onClick,
  disabled,
  loading,
  icon: Icon,
  children,
  testId,
  className = '',
  ...rest
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      data-testid={testId}
      className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed ${BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.primary} ${className}`}
      {...rest}
    >
      {loading ? <Loader2 size={15} className="animate-spin" aria-hidden /> : Icon ? <Icon size={15} aria-hidden /> : null}
      {children}
    </button>
  );
}

/** Etiket + zorunluluk yıldızı + hata metni sarmalayıcısı. */
export function Field({ id, label, required, hint, error, children }) {
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs font-bold text-[var(--text-main)]">
        {label}
        {required && <span className="text-rose-600" aria-hidden> *</span>}
      </label>
      {typeof children === 'function' ? children({ id, describedBy }) : children}
      {hint && !error && (
        <p id={`${id}-hint`} className="text-[11px] text-[var(--text-muted)]">{hint}</p>
      )}
      {error && (
        <p id={`${id}-error`} role="alert" className="text-[11px] font-bold text-rose-600">{error}</p>
      )}
    </div>
  );
}

/**
 * Senet etiketi: "SN-000123 (1/3) · ₺8.333,33 · 01.01.2027".
 * Senet numarası yoksa ham `pnote_...` id'si ASLA kullanıcıya gösterilmez —
 * PromissoryNoteList.jsx'teki "Senet numarası yok" deseniyle AYNI: tutar +
 * vade zaten ayırt edicidir. `t` verilmezse (çağıran unutursa) sessiz "—"
 * fallback'i kullanılır, yine de ham id sızmaz.
 */
export function noteOptionLabel(note, lang, t) {
  const number = splitLabel(note.note_number, note.split_index, note.split_total)
    || (t ? t('accounting.promissory_notes.no_number') : '—');
  const due = normalizeDueDate(note.due_date) || '—';
  return `${number} · ${fmtMoneyAccounting(note.amount, lang)} · ${due}`;
}

/**
 * Taksit etiketi: "Taksit 2/6 · ₺8.333,33 · 01.01.2027" (S-14 taksit seçici).
 * `noteOptionLabel` ile AYNI biçim dili — ayrı bir görsel sözlük kurulmuyor.
 */
export function installmentOptionLabel(inst, lang, t) {
  const no = inst?.installment_no || '';
  const total = inst?.total_installments || '';
  const order = no && total ? ` ${no}/${total}` : no ? ` ${no}` : '';
  const due = normalizeDueDate(inst?.due_date) || '—';
  return `${t('accounting.notes.add.installment_word')}${order} · ${fmtMoneyAccounting(inst?.amount, lang)} · ${due}`;
}

/**
 * Senet seçici. `multiple` false ise native `<select>`, true ise checkbox
 * listesi. `notes` listesi ÇAĞIRAN tarafından filtrelenir (ör. birleştirmede
 * yalnız aynı fatura + `active` senetler) — bileşen filtre kuralı bilmez.
 */
export function NotePicker({
  id,
  notes,
  value,
  onChange,
  multiple = false,
  disabled,
  label,
  required,
  hint,
  error,
  placeholder,
  emptyText,
  lang,
  t,
  testId,
}) {
  if (!notes || notes.length === 0) {
    return (
      <InlineAlert variant="info" testId={testId ? `${testId}-empty` : undefined}>
        {emptyText}
      </InlineAlert>
    );
  }

  if (!multiple) {
    return (
      <Field id={id} label={label} required={required} hint={hint} error={error}>
        {({ id: fieldId, describedBy }) => (
          <select
            id={fieldId}
            className={INPUT_CLASS}
            value={value || ''}
            disabled={disabled}
            aria-required={required || undefined}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
          >
            <option value="">{placeholder}</option>
            {notes.map((n) => (
              <option key={n.note_id} value={n.note_id}>
                {noteOptionLabel(n, lang, t)}
              </option>
            ))}
          </select>
        )}
      </Field>
    );
  }

  const selected = value || [];
  const toggle = (noteId) => {
    onChange(selected.includes(noteId) ? selected.filter((v) => v !== noteId) : [...selected, noteId]);
  };

  return (
    <div className="space-y-1">
      <p className="block text-xs font-bold text-[var(--text-main)]">
        {label}
        {required && <span className="text-rose-600" aria-hidden> *</span>}
      </p>
      <div
        role="group"
        aria-label={label}
        className="max-h-56 overflow-y-auto rounded-xl border-2 border-[var(--border-light)] divide-y divide-[var(--border-light)]"
        data-testid={testId}
      >
        {notes.map((n) => {
          const badge = promissoryStatusBadgeProps(n.status, t);
          const checked = selected.includes(n.note_id);
          return (
            <label
              key={n.note_id}
              className="flex items-center gap-2.5 p-2.5 text-xs cursor-pointer hover:bg-gray-50"
            >
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-2 border-[var(--border-light)] accent-[var(--brand-primary)]"
                checked={checked}
                disabled={disabled || isStuckNote(n.status)}
                onChange={() => toggle(n.note_id)}
                data-testid={testId ? `${testId}-item-${n.note_id}` : undefined}
              />
              <span className="flex-1 font-semibold">{noteOptionLabel(n, lang, t)}</span>
              <span className="text-[10px] text-[var(--text-muted)]">{badge.label}</span>
            </label>
          );
        })}
      </div>
      {hint && !error && <p className="text-[11px] text-[var(--text-muted)]">{hint}</p>}
      {error && <p role="alert" className="text-[11px] font-bold text-rose-600">{error}</p>}
    </div>
  );
}

/** Sekme başlığı + açıklama bloğu (5 sekmede de aynı desen). */
export function TabIntro({ title, description }) {
  return (
    <div>
      <h3 className="font-display text-base font-bold text-[var(--text-main)]">{title}</h3>
      <p className="text-xs text-[var(--text-muted)] mt-0.5">{description}</p>
    </div>
  );
}
